'use client';

import {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  captureNavBaseline,
  hasInAppHistory,
  noteNavigation,
  subscribeNavHistory,
} from '@/lib/nav-history';
import { viewHref } from '@/lib/view-href';
import { LiveConnection } from './live';
import { TapMount } from './tap-mount';
import { useTapMountStanding } from './tap-mount-store';
import { LoadingDock, LoadingTitle, useLoadingChrome } from './loading-chrome-ui';
import { usePublishShellNames } from './shell-names';
import { TapLink } from './tap-link';
import { shellBackFor } from './shell-back';
import { useBack } from './use-back';
import { LiveDevice } from './live-device';
import { CreateView } from './create-view';
import { CreateTask } from './create-task';
import { AwaitNumber } from './await-value';
import { Dock, DockButton, DockActionButton } from './dock';
import { DockActionProvider, DockNavProvider, useDockActionSlot } from './dock-action';
import { buildGlobalCommands } from './palette-commands';
import { ProfileMenu, type ProfileIdentity } from './profile-menu';
import { QuickSwitcher, type PaletteMode, type SwitcherTarget } from './quick-switcher';
import { PaletteOpenContext } from './palette-open';
import {
  TopbarSlotContext,
  TopbarRightSlotContext,
  PeekChromeContext,
  createSlotStore,
  useSlotFilled,
  type PeekChrome,
} from './topbar-slot';
import { Dialog, IconButton, Menu } from './ui';
import { useTheme } from './ui/use-theme';
import { ViewStar } from './view-star';
import { LAYOUT_GLYPH } from './layout-glyph';
import {
  House,
  Graph,
  Hash,
  Aperture,
  SquaresFour,
  Steps,
  FlowArrow,
  Bell,
  Stack,
  CaretDoubleLeft,
  CaretDoubleRight,
  CaretLeft,
  MagnifyingGlass,
  Users,
  User,
  ArrowsDownUp,
  Plus,
} from './icons';
import { pickableActors } from '@/lib/directory';
import { useCounterparts, useDirectory } from '@/lib/directory-context';
import { useT } from '@/lib/locale-context';
import type { PlainKey } from '@/i18n';
import {
  type ViewsSort,
  type NavViewOrder,
  VIEWS_SORTS,
  VIEWS_SORT_LABEL,
  sortNavViews,
} from './nav-views-sort';
import { setViewsSort, useViewsSort } from './views-sort-pref';
import styles from './app-shell.module.css';

const STORAGE_KEY = 'tesserano_sidebar_collapsed';
// The workgraph-era key (renamed 2026-09-26): read as the fallback while the
// new key is unset, and dropped on the first write, so a stored choice
// survives the rename.
const LEGACY_STORAGE_KEY = 'wg_sidebar_collapsed';
const COLLAPSE_EVENT = 'wg:sidebar-collapsed';

// The collapse preference is external (localStorage) state, so read it through
// useSyncExternalStore rather than a setState-in-effect: it hydrates without a
// mismatch (server snapshot is `false`, the client re-reads after mount) and a
// same-tab change is broadcast via a custom event.
function subscribeCollapsed(onChange: () => void): () => void {
  window.addEventListener('storage', onChange);
  window.addEventListener(COLLAPSE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(COLLAPSE_EVENT, onChange);
  };
}
function readCollapsed(): boolean {
  try {
    return (localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY)) === '1';
  } catch {
    /* private mode / disabled storage: the expanded default */
    return false;
  }
}
function setCollapsedPref(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* storage unavailable: the choice cannot be kept, and the read stays expanded */
  }
  window.dispatchEvent(new Event(COLLAPSE_EVENT));
}

// How many rows a nav section shows before "Show more" (Leon, 2026-09-11): a
// sidebar that lists every lens and every view stops being navigation. The cap
// is per SECTION and never applies to Pinned - those rows were chosen.
const NAV_SECTION_CAP = 7;

// Which sections the viewer has expanded past the cap - a personal, client-only
// preference like the sort, stored as a comma list ("lenses,views").
const NAV_MORE_KEY = 'wg_nav_more';
const NAV_MORE_EVENT = 'wg:nav-more';
function subscribeNavMore(onChange: () => void): () => void {
  window.addEventListener('storage', onChange);
  window.addEventListener(NAV_MORE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(NAV_MORE_EVENT, onChange);
  };
}
function readNavMore(): string {
  return localStorage.getItem(NAV_MORE_KEY) ?? '';
}
function toggleNavMore(what: NavSectionKey): void {
  const open = new Set(readNavMore().split(',').filter(Boolean));
  if (open.has(what)) open.delete(what);
  else open.add(what);
  localStorage.setItem(NAV_MORE_KEY, [...open].join(','));
  window.dispatchEvent(new Event(NAV_MORE_EVENT));
}

export interface NavGroup {
  id: string;
  name: string;
}

// The nav sort fields (name, kind, layout, createdAt, changedAt, lastViewedAt)
// come from NavViewOrder; the rest is nav-render-only. `kind` drives the
// destination and the section: `lens` rows sit under **Lenses** (Views in the
// UI), boards under **Views** (Boards in the UI) - the docs/LENSES.md §7 kind
// split. A board's row wears the glyph of its default layout (`layout`), a
// lens's the view glyph.
export interface NavView extends NavViewOrder {
  id: string;
  /** the viewer has starred this view: it floats into the Favorites group. */
  starred: boolean;
}

/**
 * Routes that render without the app's chrome even for a signed-in person: no
 * sidebar, top bar, dock or create button. /connect is a consent page an app
 * like Claude sends the browser to (docs/AGENT_ACCESS.md §6a step 4): it
 * stands alone like /signin, so nothing of the app surrounds the question or
 * covers its buttons on a phone. /signin needs no entry: it renders for an
 * anonymous request, which gets no shell at all (src/app/layout.tsx).
 */
const STANDALONE_ROUTES: readonly string[] = ['/connect'];

function isStandaloneRoute(pathname: string): boolean {
  return STANDALONE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

/** The nav row's glyph (one vocabulary with the palette rows): a board's
 *  default layout (the layout glyphs' one source, `layout-glyph.tsx`), the
 *  view glyph for a lens. */
function viewIcon(view: NavView): React.ReactNode {
  return view.kind === 'board' && view.layout ? LAYOUT_GLYPH[view.layout] : <Aperture />;
}

function NavItem({
  href,
  label,
  icon,
  active,
  collapsed,
  unread,
}: {
  href: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  collapsed: boolean;
  /**
   * Streamed unread count; >0 shows a badge (a bare dot when collapsed). Only
   * the badge element suspends (see await-value.tsx) - the item itself paints
   * immediately.
   */
  unread?: Promise<number>;
}) {
  const t = useT();
  return (
    <TapLink
      href={href}
      // The tap names its target, so the shell's loading chrome titles it at
      // once (docs/LOADING.md §4.2).
      tap={{ title: label }}
      className={styles.navItem}
      data-active={active || undefined}
      title={collapsed ? label : undefined}
      aria-current={active ? 'page' : undefined}
    >
      <span className={styles.navIcon}>
        {icon}
        {unread && collapsed && (
          <Suspense fallback={null}>
            <AwaitNumber value={unread}>
              {(n) => (n > 0 ? <span className={styles.navDot} aria-hidden /> : null)}
            </AwaitNumber>
          </Suspense>
        )}
      </span>
      {!collapsed && <span className={styles.navLabel}>{label}</span>}
      {!collapsed && unread && (
        <Suspense fallback={null}>
          <AwaitNumber value={unread}>
            {(n) =>
              n > 0 ? (
                <span
                  // eslint-disable-next-line no-restricted-syntax -- an unread counter on an icon's corner, not a chip (docs/DESIGN_SYSTEM.md "Chip"; the CountBadge follow-up in the board item "The one chip: what the wave left open")
                  className={styles.navBadge}
                  aria-label={t('shell.nav.unread', { count: n })}
                >
                  {n > 99 ? '99+' : n}
                </span>
              ) : null
            }
          </AwaitNumber>
        </Suspense>
      )}
    </TapLink>
  );
}

/** One nav Views row: the link (via NavItem) plus its favorite toggle, in a hover
 *  group so the star reveals on hover (the star is a sibling of the Link, never
 *  nested inside it). The star is hidden when collapsed - no room in the rail. */
function ViewNavRow({
  view,
  pathname,
  collapsed,
}: {
  view: NavView;
  pathname: string;
  collapsed: boolean;
}) {
  return (
    <div className={styles.viewRow}>
      <NavItem
        href={viewHref(view.kind, view.id)}
        label={view.name}
        icon={viewIcon(view)}
        active={pathname === viewHref(view.kind, view.id)}
        collapsed={collapsed}
      />
      {!collapsed && (
        <ViewStar viewId={view.id} starred={view.starred} className={styles.viewStar} />
      )}
    </div>
  );
}

/** The two capped nav sections. Pinned is deliberately not one: those rows
 *  were chosen by the viewer, so they are never hidden behind "Show more". */
type NavSectionKey = 'lenses' | 'views';

/** The overview each section heading links to - "all of them", the page the
 *  sidebar is a shortlist of. */
const NAV_SECTION_HREF: Record<NavSectionKey, string> = {
  lenses: '/views',
  views: '/boards',
};
/** Each section's words, as catalog keys: its heading, the heading link's
 *  title, the "+", and the sort menu's names. */
const NAV_SECTION_WORDS: Record<
  NavSectionKey,
  { label: PlainKey; all: PlainKey; new: PlainKey; sort: PlainKey }
> = {
  lenses: {
    label: 'shell.page.lenses',
    all: 'shell.nav.allLenses',
    new: 'shell.nav.newLens',
    sort: 'shell.nav.sortLenses',
  },
  views: {
    label: 'shell.page.views',
    all: 'shell.nav.allViews',
    new: 'shell.nav.newView',
    sort: 'shell.nav.sortViews',
  },
};

/**
 * One nav section: the heading (a LINK to the section's overview, with its
 * sort menu and "+"), at most `NAV_SECTION_CAP` rows, and a "Show more" that
 * reveals the rest IN PLACE. Two destinations, deliberately split (Leon,
 * 2026-09-11): more rows here, or the whole index behind the heading.
 */
function NavSection({
  what,
  rows,
  expanded,
  viewsSort,
  onNew,
  newTestId,
  pathname,
  collapsed,
}: {
  what: NavSectionKey;
  rows: NavView[];
  expanded: boolean;
  viewsSort: ViewsSort;
  onNew: () => void;
  newTestId: string;
  pathname: string;
  collapsed: boolean;
}) {
  const t = useT();
  const shown = expanded ? rows : rows.slice(0, NAV_SECTION_CAP);
  const hidden = rows.length - shown.length;
  const words = NAV_SECTION_WORDS[what];
  return (
    <>
      {!collapsed && (
        <div className={styles.navSectionRow}>
          <Link
            href={NAV_SECTION_HREF[what]}
            className={styles.navSection}
            title={t(words.all)}
            data-testid={`nav-section-${what}`}
          >
            {t(words.label)}
          </Link>
          <div className={styles.sectionActions}>
            {rows.length > 1 && <SectionSortMenu viewsSort={viewsSort} what={what} />}
            <IconButton
              label={t(words.new)}
              onClick={onNew}
              className={styles.sectionAction}
              data-testid={newTestId}
            >
              <Plus aria-hidden />
            </IconButton>
          </div>
        </div>
      )}
      {shown.map((v) => (
        <ViewNavRow key={v.id} view={v} pathname={pathname} collapsed={collapsed} />
      ))}
      {!collapsed && (hidden > 0 || expanded) && (
        <button
          type="button"
          className={styles.navMore}
          onClick={() => toggleNavMore(what)}
          data-testid={`nav-more-${what}`}
        >
          {hidden > 0 ? t('shell.nav.showMore', { count: hidden }) : t('shell.nav.showLess')}
        </button>
      )}
    </>
  );
}

/** One section header's sort menu (Lenses / Views share the one stored
 *  preference, so either menu sets the order of both lists). */
function SectionSortMenu({ viewsSort, what }: { viewsSort: ViewsSort; what: 'lenses' | 'views' }) {
  const t = useT();
  const sort = t(VIEWS_SORT_LABEL[viewsSort]);
  return (
    <Menu
      ariaLabel={t(NAV_SECTION_WORDS[what].sort)}
      align="end"
      trigger={({ toggle }) => (
        <IconButton
          label={
            what === 'lenses'
              ? t('shell.nav.sortLensesBy', { sort })
              : t('shell.nav.sortViewsBy', { sort })
          }
          onClick={toggle}
          className={styles.sectionAction}
        >
          <ArrowsDownUp aria-hidden />
        </IconButton>
      )}
      items={[
        { kind: 'label', label: t('shell.nav.sortBy') },
        ...VIEWS_SORTS.map((s) => ({
          label: t(VIEWS_SORT_LABEL[s]),
          selected: s === viewsSort,
          onSelect: () => setViewsSort(s),
        })),
      ]}
    />
  );
}

/**
 * The app shell: a collapsible left sidebar for navigation and a top bar whose
 * right side carries the profile menu (with the view-as persona picker). Wraps
 * every page. The intercepted item peek (`modal`) seats as an overlay INSIDE
 * the content column, so the sidebar and top bar stay visible and interactive
 * over an open peek (the fullest screen is a collapsed sidebar, not a
 * full-viewport page). Collapse state persists in localStorage. Nav links are
 * plain URLs (nav-pages), so opening them clears the "how I got here" trail,
 * per the navigation model.
 */
export function AppShell({
  viewer,
  devMode = false,
  operator = false,
  identity = null,
  groups,
  views,
  unread,
  children,
  modal,
}: {
  viewer: string;
  /** Dev-only: render the quick persona switcher (real sign-in). Never set in production. */
  devMode?: boolean;
  /** The viewer is an operator of this instance (src/read/operators.ts). */
  operator?: boolean;
  /** The viewer's handle and sign-in address, for the profile menu's head. */
  identity?: ProfileIdentity | null;
  groups: NavGroup[];
  views: NavView[];
  /** Unread inbox count for the Activity badge, streamed (see WithUnread). */
  unread: Promise<number>;
  children: React.ReactNode;
  /** The intercepted item peek (@modal slot). Seated as an overlay inside the
   *  content column so the sidebar + top bar stay (PeekChromeContext); must
   *  render INSIDE the Dock providers so the peek's ItemDock reaches the dock's
   *  contextual slots. */
  modal?: React.ReactNode;
}) {
  const t = useT();
  const collapsed = useSyncExternalStore(subscribeCollapsed, readCollapsed, () => false);
  const directory = useDirectory();
  const counterparts = useCounterparts();
  // The nav Views order: the one views-sort preference (views-sort-pref.ts),
  // which the overviews order by too.
  const viewsSort = useViewsSort();
  const sortedViews = sortNavViews(views, viewsSort);
  // Which nav sections are expanded past the cap (personal, localStorage).
  const navMore = useSyncExternalStore(subscribeNavMore, readNavMore, () => '');
  const navMoreOpen = new Set(navMore.split(',').filter(Boolean));
  // Favorites (starred) float into their own group at the top; the chosen sort
  // still orders within each group.
  const favoriteViewsList = sortedViews.filter((v) => v.starred);
  const otherViews = sortedViews.filter((v) => !v.starred);
  // The KIND split (docs/LENSES.md §7): **Lenses** - shared ways of looking,
  // touching no one - and **Views** - the audience'd boards. This
  // replaces the old personal/shared audience split; both sections are always
  // present, each with its own inline "+".
  const navLenses = otherViews.filter((v) => v.kind === 'lens');
  const navTables = otherViews.filter((v) => v.kind !== 'lens');
  // Start tracking the history position before any in-app navigation, so the
  // Back-as-close affordances can tell "pop stays in-app" from "pop leaves"
  // (src/lib/nav-history.ts).
  useEffect(() => {
    captureNavBaseline();
  }, []);
  const pathname = usePathname();
  // The router writes a navigation's history entry in the commit that brings
  // the new pathname, after this render read `hasInAppHistory`; the effect
  // runs after that write, so the dock's Back asks again (subscribeNavHistory).
  useEffect(() => {
    noteNavigation();
  }, [pathname]);
  // The palette (overlay affordance): quick-open from the dock's Search item,
  // the topbar trigger, and Ctrl/Cmd+O / Ctrl/Cmd+K anywhere; command mode via
  // Ctrl/Cmd+P (or ">"). Targets are the nav-pages + boards the shell knows.
  const [palette, setPalette] = useState<{ open: boolean; mode: PaletteMode; seed?: string }>({
    open: false,
    mode: 'open',
  });
  // `seed` rides only a deliberate open-with-text (the Refine escalation, below);
  // every other open (Ctrl+O, the topbar/dock triggers) omits it, so it clears.
  const openPalette = (open: boolean, mode?: PaletteMode, seed?: string) =>
    setPalette((p) => ({ open, mode: mode ?? p.mode, seed: open ? seed : undefined }));
  // The Find-palette opener published to descendant Refine surfaces
  // (docs/VIEWS.md §12): a local dead-end hands its text off to global Find.
  const paletteOpener = useMemo(
    () => ({ open: (query?: string) => openPalette(true, 'open', query) }),
    [],
  );
  // The shell-level create surfaces (reachable from the nav headers' "+"s, the
  // palette, and the dock alike). The create modal is ONE two-question surface
  // (docs/LENSES.md §7); which answer it opens on rides along.
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState<null | 'lens' | 'view'>(null);
  const { setPref: setThemePref } = useTheme();
  const switcherTargets: SwitcherTarget[] = [
    { href: '/activity', label: t('shell.page.activity'), kind: 'page', icon: <Bell /> },
    { href: '/work', label: t('shell.page.work'), kind: 'page', icon: <House /> },
    { href: '/map', label: t('shell.page.map'), kind: 'page', icon: <Graph /> },
    { href: '/tags', label: t('shell.page.tags'), kind: 'page', icon: <Hash /> },
    { href: '/groups', label: t('shell.page.groups'), kind: 'page', icon: <Users /> },
    { href: '/flow-phases', label: t('shell.page.flowPhases'), kind: 'page', icon: <Steps /> },
    { href: '/flows', label: t('shell.page.flows'), kind: 'page', icon: <FlowArrow /> },
    { href: '/plans', label: t('shell.page.plans'), kind: 'page', icon: <Stack /> },
    { href: '/ceremonies', label: t('shell.page.ceremonies'), kind: 'page', icon: <Users /> },
    {
      href: '/search',
      label: t('shell.page.search'),
      kind: 'page',
      icon: <MagnifyingGlass />,
    },
    ...groups.map(
      (t): SwitcherTarget => ({
        href: `/groups/${t.id}`,
        label: t.name,
        kind: 'group',
        icon: <Users />,
      }),
    ),
    ...sortedViews.map(
      // A board or a view names no scope (no board is a group's, Leon
      // 2026-10-26): the row shows its icon and kind badge, and fuzzy matches
      // the NAME.
      (v): SwitcherTarget => ({
        href: viewHref(v.kind, v.id),
        label: v.name,
        kind: v.kind,
        icon: viewIcon(v),
      }),
    ),
  ];
  const paletteCommands = buildGlobalCommands({
    navTargets: switcherTargets,
    onNewItem: () => setNewItemOpen(true),
    onNewLens: () => setCreateOpen('lens'),
    onNewView: () => setCreateOpen('view'),
    setTheme: setThemePref,
  });
  // The narrow-by catalogs: groups the shell knows, people from the client
  // directory (whom this viewer may name and pick; docs/SEARCH.md). PERSONS
  // only - `groups` above is the group catalog, and letting group refs into
  // both made one group offer two identically-named suggestions.
  const suggestCtx = useMemo(
    () => ({
      groups: groups.map((t) => ({ id: t.id, name: t.name })),
      actors: pickableActors(directory, { kind: 'person' }),
      // The palette holds no rows to name parents from (the board item "The
      // Work row's parent path, "+N" and due date: the read slice", "The parent
      // filter's catalog"): the parent attribute lists nothing here, and the
      // tag attribute neither.
      parents: [],
      tags: [],
      selfRef: viewer,
      // The person shortlist's order for the two seat holders' value lists
      // (docs/DESIGN_SYSTEM.md "The person shortlist").
      ranked: counterparts.ranked,
    }),
    [groups, directory, viewer, counterparts.ranked],
  );
  // The floating dock's page-action slot (dock-action.tsx): pages claim it
  // with DockAction; unclaimed, the shell's default create rides there ("+"
  // opening the New-item dialog; on the /boards index, the New-board surface).
  const dockSlot = useDockActionSlot();
  // The dock's NAV override slot (same registry shape): a page claims the
  // whole nav region - the item page's contextual Back / "…" row replaces
  // the four nav homes while an item surface is up (docs/MOBILE.md §2.4).
  const dockNavSlot = useDockActionSlot();
  // The loading chrome (docs/LOADING.md §4.2): the top bar's title or trail
  // and the dock while a surface loads, drawn here because the shell hydrates
  // where a streamed skeleton does not (a direct visit, a refresh).
  const navNames = useMemo(() => ({ views, groups }), [views, groups]);
  const chrome = useLoadingChrome(dockNavSlot.claimed, navNames);
  // The dock's Back, its first slot on compact (docs/MOBILE.md §2.4), speaks
  // for the page the chrome shows: the tap's target while the tap's mount
  // stands. A dock home has no fallback, so there Back is drawn disabled while
  // it has nothing to undo: no in-app history and no tap's mount standing.
  // Both read as false on the server, so a home's Back never flashes lit.
  const backFallback = shellBackFor(chrome.shown);
  const back = useBack(backFallback);
  const inAppHistory = useSyncExternalStore(subscribeNavHistory, hasInAppHistory, () => false);
  const tapMountStanding = useTapMountStanding();
  const backDisabled = backFallback === null && !inAppHistory && !tapMountStanding;
  // The same names for a route's error screen, which titles the top bar the
  // way the chrome does (shell-names.ts).
  usePublishShellNames(navNames);
  // The top bar's left region: pages portal their header (title, count, members)
  // into it via TopbarSlot, so there is one bar, not two stacked ones.
  // Both are stores, never state: a slot context's value must not change on
  // mount (SlotStore, topbar-slot.tsx).
  const [topbarSlot] = useState(createSlotStore);
  // The bar's right region (left of the profile menu): group-scoped pages portal their
  // members face-pile here, so "who has access" sits beside "my account".
  const [topbarRightSlot] = useState(createSlotStore);
  const topbarSlotRef = useCallback((el: HTMLElement | null) => topbarSlot.set(el), [topbarSlot]);
  const topbarFilled = useSlotFilled(topbarSlot);
  const topbarRightSlotRef = useCallback(
    (el: HTMLElement | null) => topbarRightSlot.set(el),
    [topbarRightSlot],
  );
  // The top bar's PEEK slot + the active flag (peek-chrome seam, 2026-07-20):
  // the intercepted item peek seats inside the content column and portals its
  // path trail here, mirroring the direct-visit page. While a peek is up
  // (`peekActive`) the shell swaps the underlying page's header out for the
  // peek trail (`data-peek`), so the top bar shows the item trail, not the list
  // it opened from - visually identical to a refresh / shared-link render.
  const [topbarPeekSlot, setTopbarPeekSlot] = useState<HTMLElement | null>(null);
  // A COUNT of the peeks up, not a flag: two can be mounted at once (the
  // tap's mount over a peek on a drill, docs/LOADING.md §2 tool 3), and the
  // one that leaves first must not lower the flag for the one that stays.
  const [peekCount, setPeekCount] = useState(0);
  const peekActive = peekCount > 0;
  const setPeekActive = useCallback(
    (active: boolean) => setPeekCount((n) => Math.max(0, n + (active ? 1 : -1))),
    [],
  );
  const peekChrome = useMemo<PeekChrome>(
    () => ({ topbarEl: topbarPeekSlot, active: peekActive, setActive: setPeekActive }),
    [topbarPeekSlot, peekActive, setPeekActive],
  );

  function toggle() {
    setCollapsedPref(!collapsed);
  }

  // A page that stands alone (STANDALONE_ROUTES): the page itself, inside the
  // layout's providers only, as an anonymous request renders /signin. Decided
  // after every hook, so the hook order never depends on the route.
  if (isStandaloneRoute(pathname)) return <>{children}</>;

  return (
    <div
      className={styles.shell}
      data-collapsed={collapsed ? 'true' : undefined}
      data-peek={peekActive ? 'true' : undefined}
      data-loading={chrome.overridesBar ? 'true' : undefined}
      data-testid="app-shell"
    >
      <LiveConnection />
      <aside className={styles.sidebar} aria-label={t('shell.nav.primary')}>
        <div className={styles.brand}>
          <Link href="/work" className={styles.mark} aria-label={t('shell.brand.home')}>
            <span className={styles.markDot} />
            {!collapsed && <span className={styles.wordmark}>{t('shell.brand.name')}</span>}
          </Link>
        </div>

        <nav className={styles.nav}>
          <NavItem
            href="/activity"
            label={t('shell.page.activity')}
            icon={<Bell />}
            active={pathname === '/activity' || pathname.startsWith('/activity/')}
            collapsed={collapsed}
            unread={unread}
          />
          <NavItem
            href="/work"
            label={t('shell.page.work')}
            icon={<House />}
            active={pathname === '/work'}
            collapsed={collapsed}
          />
          <NavItem
            href="/map"
            label={t('shell.page.map')}
            icon={<Graph />}
            active={pathname === '/map'}
            collapsed={collapsed}
          />
          <NavItem
            href="/tags"
            label={t('shell.page.tags')}
            icon={<Hash />}
            active={pathname === '/tags'}
            collapsed={collapsed}
          />
          <NavItem
            href="/groups"
            label={t('shell.page.groups')}
            icon={<Users />}
            active={pathname === '/groups' || pathname.startsWith('/groups/')}
            collapsed={collapsed}
          />
          <NavItem
            href="/flow-phases"
            label={t('shell.page.flowPhases')}
            icon={<Steps />}
            active={pathname === '/flow-phases'}
            collapsed={collapsed}
          />
          <NavItem
            href="/flows"
            label={t('shell.page.flows')}
            icon={<FlowArrow />}
            active={pathname === '/flows' || pathname.startsWith('/flows/')}
            collapsed={collapsed}
          />
          <NavItem
            href="/plans"
            label={t('shell.page.plans')}
            icon={<Stack />}
            active={pathname === '/plans' || pathname.startsWith('/plans/')}
            collapsed={collapsed}
          />
          <NavItem
            href="/ceremonies"
            label={t('shell.page.ceremonies')}
            icon={<Users />}
            active={pathname === '/ceremonies' || pathname.startsWith('/ceremonies/')}
            collapsed={collapsed}
          />
          {/* Groups are a nav *page* (the "Groups" item above -> /groups, a card per
              group), not a rail of direct links. Views stay a section here: a view
              (board or saved map view) is a lens, and creating one never touches a
              group (CONTRACT_SPINE §1, D5). Starred views float into a Favorites
              group at the top; a per-row star toggles it (private, off the log). */}
          {favoriteViewsList.length > 0 && (
            <>
              {!collapsed && <div className={styles.navSection}>{t('shell.nav.pinned')}</div>}
              {favoriteViewsList.map((v) => (
                <ViewNavRow key={v.id} view={v} pathname={pathname} collapsed={collapsed} />
              ))}
            </>
          )}
          {/* The KIND split (docs/LENSES.md §7): **Lenses** - shared ways of
              looking, opener-relative, touching no one - then **Views** -
              audience'd tables (board / list / map). Both sections are ALWAYS
              present (an empty section's "+" invites the first one); each
              heading carries its inline "+" and (with enough rows) the sort
              menu, and LINKS to that kind's index - the sidebar is a
              shortlist, the index is all of them. Past NAV_SECTION_CAP rows a
              "Show more" reveals the rest in place. Task creation lives in the
              palette / dock / content head, not here. */}
          <NavSection
            what="lenses"
            rows={navLenses}
            expanded={navMoreOpen.has('lenses')}
            viewsSort={viewsSort}
            onNew={() => setCreateOpen('lens')}
            newTestId="nav-new-lens"
            pathname={pathname}
            collapsed={collapsed}
          />
          <NavSection
            what="views"
            rows={navTables}
            expanded={navMoreOpen.has('views')}
            viewsSort={viewsSort}
            onNew={() => setCreateOpen('view')}
            newTestId="nav-new-view"
            pathname={pathname}
            collapsed={collapsed}
          />
        </nav>

        <button
          type="button"
          className={styles.collapse}
          onClick={toggle}
          aria-label={collapsed ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
          title={collapsed ? t('shell.sidebar.expandShort') : t('shell.sidebar.collapseShort')}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <CaretDoubleRight className={styles.collapseIcon} aria-hidden />
          ) : (
            <CaretDoubleLeft className={styles.collapseIcon} aria-hidden />
          )}
          {!collapsed && (
            <span className={styles.collapseLabel}>{t('shell.sidebar.collapseShort')}</span>
          )}
        </button>
      </aside>

      <div className={styles.mainCol}>
        <header className={styles.topbar}>
          {/* Two left slots share the same column: the base slot (the page's
              own header) and the peek slot (the intercepted item's path trail).
              While a peek is up, `data-peek` on the shell hides the base and
              shows the peek slot, so the top bar reads the item, not the list
              it opened from. */}
          <div className={styles.topbarSlot} ref={topbarSlotRef} />
          {/* The loading chrome's default seat: right after the base slot,
              whose emptiness decides whether it shows (the CSS module). */}
          {/* Rendered only while nothing else holds the bar: a page's own
              header in the base slot, or a peek's trail, takes it out of the
              DOM (the CSS rules stay for the frame before hydration), unless
              the tap's mount stands for a new page (`overridesBar`). */}
          {(chrome.overridesBar || (!topbarFilled && !peekActive)) && (
            <LoadingTitle chrome={chrome} className={styles.topbarDefault ?? ''} />
          )}
          <div className={styles.topbarPeekSlot} ref={setTopbarPeekSlot} />
          <div className={styles.topRight}>
            {/* Members face-pile (group-scoped pages portal here); a divider shows only
                when it has content, then the personal/account menu, farthest right. */}
            <div className={styles.topRightSlot} ref={topbarRightSlotRef} />
            {/* The device store, the offline queue's replay, the service
                worker, and the quiet "N waiting" count (docs/SYNC.md §7.4).
                Renders nothing while the queue is empty. */}
            <LiveDevice viewer={viewer} />
            {/* Global search. Hidden on compact (`.topRight .barSearch` in the
                CSS module): the dock owns the Search lane there, and two identical
                magnifiers on one phone screen is the duplicate Leon hit on
                2026-09-12 - both global, neither about the surface. A
                surface's OWN search belongs in its content head. */}
            <IconButton
              className={styles.barSearch}
              label={t('shell.topbar.search')}
              onClick={() => openPalette(true, 'open')}
            >
              <MagnifyingGlass aria-hidden />
            </IconButton>
            <ProfileMenu
              viewer={viewer}
              identity={identity}
              devMode={devMode}
              operator={operator}
              compact
            />
          </div>
        </header>
        {/* The content column is the peek's positioned containing block: the
            page scrolls in an inner wrapper, and the intercepted peek renders
            as an absolute overlay filling this column (over the page, which
            stays mounted). The peek MUST sit inside the Dock providers so its
            ItemDock reaches the dock's contextual slots (mobile-shell e2e,
            2026-07-20). */}
        <div className={styles.content}>
          <PaletteOpenContext.Provider value={paletteOpener}>
            <TopbarSlotContext.Provider value={topbarSlot}>
              <TopbarRightSlotContext.Provider value={topbarRightSlot}>
                <DockActionProvider value={dockSlot.registry}>
                  <DockNavProvider value={dockNavSlot.registry}>
                    <div className={styles.contentScroll} data-testid="content-scroll">
                      {children}
                    </div>
                    {/* The peek slot sits in a KEYED position (2026-09-15): `modal`
                          arrives from the server as a lazy Flight chunk, so the
                          static-children check cannot mark it validated, and the
                          reconciler then finds an unkeyed element at index 1 of
                          this array - the "unique key" warning every dev page
                          carried. The Fragment makes the slot the array member
                          (keyed by its name) and the slot's element its sole
                          child; no DOM, no layout change. */}
                    <Fragment key="peek">
                      {/* Around the peek slot only: the chrome's value changes on
                          mount and on every peek, and must not reach the page's
                          streamed boundaries (topbar-slot.tsx). */}
                      <PeekChromeContext.Provider value={peekChrome}>
                        {modal}
                      </PeekChromeContext.Provider>
                    </Fragment>
                    {/* The skeleton mounted by the tap (docs/LOADING.md §2,
                        tool 3): after the peek slot, so it draws over a peek
                        already up, and inside the peek chrome, which it
                        flags as a peek does. */}
                    <Fragment key="tap">
                      <PeekChromeContext.Provider value={peekChrome}>
                        <TapMount />
                      </PeekChromeContext.Provider>
                    </Fragment>
                  </DockNavProvider>
                </DockActionProvider>
              </TopbarRightSlotContext.Provider>
            </TopbarSlotContext.Provider>
          </PaletteOpenContext.Provider>
        </div>
        {/* The compact arrangement's dock (docs/MOBILE.md §2.4): always in the DOM,
            revealed by the phone breakpoint in its own CSS (the rendering rule). */}
        <Dock
          // Back as the default row's first slot; a page that claims the nav
          // region draws its own row (the item page's Back, the admin Home).
          back={{
            label: t('shell.trail.back'),
            icon: <CaretLeft />,
            onClick: back,
            disabled: backDisabled,
            testId: 'dock-shell-back',
          }}
          // While an item surface loads, the shell draws its row: over the
          // page below while the tap's mount stands, and until the page
          // claims the nav region otherwise (loading-chrome.ts).
          navClaimed={dockNavSlot.claimed || chrome.dock !== null}
          navOverride={
            chrome.dock ? (
              <LoadingDock kind={chrome.dock} target={chrome.target} />
            ) : (
              dockNavSlot.action
            )
          }
          items={[
            {
              href: '/activity',
              label: t('shell.page.activity'),
              icon: <Bell />,
              active: pathname === '/activity' || pathname.startsWith('/activity/'),
              unread,
            },
            {
              href: '/work',
              label: t('shell.page.work'),
              icon: <House />,
              active: pathname === '/work',
            },
            {
              // The Boards lane (Leon, 2026-07-11; named Boards 2026-10-24):
              // the lane lands on the /boards overview (the board cards and
              // the Browse footer), and stays lit while any board or view
              // surface is open.
              href: '/boards',
              label: t('shell.page.views'),
              icon: <SquaresFour />,
              active:
                pathname === '/map' ||
                pathname.startsWith('/boards') ||
                pathname.startsWith('/views'),
            },
          ]}
          action={
            // The action slot stays empty while an item surface loads, as
            // `ItemDock` and `SectionDock` claim it.
            chrome.dock ? null : dockSlot.claimed ? (
              dockSlot.action
            ) : (
              <DockActionButton
                label={pathname === '/boards' ? t('shell.nav.newView') : t('shell.nav.newTask')}
                testId="dock-action"
                onClick={() =>
                  pathname === '/boards' ? setCreateOpen('view') : setNewItemOpen(true)
                }
              >
                <Plus aria-hidden />
              </DockActionButton>
            )
          }
        >
          <DockButton
            label={t('shell.page.search')}
            icon={<MagnifyingGlass />}
            onClick={() => openPalette(true, 'open')}
          />
        </Dock>
      </div>
      <QuickSwitcher
        open={palette.open}
        mode={palette.mode}
        onOpenChange={openPalette}
        targets={switcherTargets}
        commands={paletteCommands}
        suggestCtx={suggestCtx}
        seedQuery={palette.seed}
        lazyIcons={{
          flow: <FlowArrow />,
          plan: <Stack />,
          ceremony: <Users />,
          person: <User />,
        }}
      />
      {/* The shell's create surfaces: "New item" hosts the existing
          floating-create affordance in a Dialog; the two-question create modal
          (docs/LENSES.md §7) mounts on demand, opened on whichever answer the
          invoking affordance names (the Lenses "+" on lens, everything else on
          view). */}
      <Dialog
        open={newItemOpen}
        onClose={() => setNewItemOpen(false)}
        title={t('shell.newItem.title')}
        testId="palette-new-item"
      >
        <CreateTask autoFocus onCreated={() => setNewItemOpen(false)} />
      </Dialog>
      {createOpen !== null && (
        <CreateView open initial={createOpen} onOpenChange={(o) => !o && setCreateOpen(null)} />
      )}
    </div>
  );
}
